import type { ButtonHTMLAttributes, ComponentProps, ReactNode } from 'react'
import { Link } from '@tanstack/react-router'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

const base =
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none'

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-9 px-4 text-sm',
  lg: 'h-10 px-5 text-sm',
}

const variants: Record<Variant, string> = {
  primary:
    'bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] shadow-sm',
  secondary:
    'border border-border bg-surface text-fg hover:bg-surface-subtle shadow-sm',
  ghost:
    'text-fg hover:bg-surface-subtle',
  danger:
    'bg-danger text-white hover:opacity-90 shadow-sm',
}

function classes(variant: Variant, size: Size, extra = ''): string {
  return `${base} ${sizes[size]} ${variants[variant]} ${extra}`
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  children: ReactNode
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  className = '',
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={classes(variant, size, className)}
    >
      {loading ? (
        <>
          <span className="inline-block h-3 w-3 animate-spin rounded-full border border-current border-t-transparent" />
          <span className="opacity-80">Working…</span>
        </>
      ) : (
        children
      )}
    </button>
  )
}

/**
 * `LinkButton` renders a TanStack Router Link styled like a Button. Use this
 * for navigation actions in PageHeader/Card slots — never nest <Button> inside
 * <Link> (invalid HTML5 and breaks click handling in some browsers).
 */
type LinkProps = ComponentProps<typeof Link>
interface LinkButtonProps extends LinkProps {
  variant?: Variant
  size?: Size
  children: ReactNode
}

export function LinkButton({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...rest
}: LinkButtonProps) {
  return (
    <Link {...rest} className={classes(variant, size, className)}>
      {children}
    </Link>
  )
}
